'use client';
import { useEffect } from 'react';
import Header from'@/components/common/Header';
import Footer from'@/components/common/Footer';
import ContactForm from'./components/ContactForm';
import Icon from'@/components/ui/AppIcon';
import Link from'next/link';

export default function ContactPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  const faqs = [
    {
      id: 'faq_order',
      question: 'How do I place an order?',
      answer: 'Just fill out our contact form with your requirements. We\'ll get back to you within 24 hours with a detailed quote and next steps.',
      icon: 'ShoppingCartIcon',
    },
    {
      id: 'faq_payment',
      question: 'What payment terms do you offer?',
      answer: 'We offer flexible payment terms including wire transfer, LC, and installment options for bulk orders. Terms are based on order volume.',
      icon: 'CreditCardIcon',
    },
    {
      id: 'faq_shipping',
      question: 'Do you ship worldwide?',
      answer: 'Yes! We ship to 25+ countries globally. Delivery times vary by destination, typically 2-6 weeks. We handle all customs paperwork.',
      icon: 'TruckIcon',
    },
  ]

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="max-w-container mx-auto px-6 md:px-12">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Let's Talk
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Need a quote? Have questions about our products? Our team is here to help you find the perfect supply solution.
            </p>
          </div>

          {/* Contact Form & Info Section */}
          <div className="grid lg:grid-cols-12 gap-12 mb-24">
            {/* Form - Left 7 columns */}
            <div className="lg:col-span-7 reveal">
              <ContactForm />
            </div>

            {/* Contact Info - Right 5 columns */}
            <div className="lg:col-span-5 reveal">
              <div className="bg-muted rounded-organic p-8 sticky top-24">
                <h3 className="text-2xl font-serif font-bold text-foreground mb-6">
                  Contact Information
                </h3>

                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="MapPinIcon" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Address</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Rue du Trilogiport 6/Bât 1B<br />
                        4681 Oupeye, Belgium
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="PhoneIcon" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Phone</h4>
                      <p className="text-sm text-muted-foreground">
                        <a href="tel:+3241234567" className="hover:text-primary transition-colors">
                          +32 4 123 4567
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="EnvelopeIcon" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Email</h4>
                      <p className="text-sm text-muted-foreground">
                        <a href="mailto:info@suppliesdistributorssa.com" className="hover:text-primary transition-colors">
                          info@suppliesdistributorssa.com
                        </a>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        (Forwards to: suppliesdistributorssa@gmail.com)
                      </p>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="ClockIcon" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Business Hours</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Mon-Fri: 9:00 AM - 6:00 PM CET<br />
                        Sat: 10:00 AM - 2:00 PM CET<br />
                        Sun: Closed
                      </p>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div className="pt-6 border-t border-border">
                    <h4 className="font-semibold text-foreground mb-4">Follow Us</h4>
                    <div className="flex gap-3">
                      <a
                        href="#"
                        className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <Icon name="BuildingOffice2Icon" size={20} className="text-primary" />
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                        aria-label="Facebook"
                      >
                        <Icon name="UserGroupIcon" size={20} className="text-primary" />
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                        aria-label="Twitter"
                      >
                        <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-primary" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Quick Links */}
          <section className="mb-24 reveal">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-foreground mb-12">
              Common Questions
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-card rounded-organic p-6 shadow-sm hover-lift reveal"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon name={faq.icon as any} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {faq.answer}
                  </p>
                  <Link
                    href="/blog"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                  >
                    Learn more
                    <Icon name="ArrowRightIcon" size={14} className="arrow-shift" />
                  </Link>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  )
}